import { useCallback, useEffect, useState } from 'react';
import type { FieldContent, JobTypeVersionContent, JobTypeVersionContentV3 } from '@capture/shared';
import { flattenToLegacyContent, migrateContentToV3 } from '@capture/shared';
import { formConfigApi, type JobTypeVersion } from '../form-config/api/client';

/** Thin `{internalName,label,isRequired}` ref — for field-picker surfaces. */
export interface JobTypeFieldRef {
  internalName: string;
  label: string;
  isRequired: boolean;
}

/** A per-field change targeted by `fieldDefinitionId` (the stable id on FieldContent). */
export interface FieldPatch {
  fieldDefinitionId: string;
  changes: Partial<FieldContent>;
}

interface UseJobTypeRegistryFieldsResult {
  /** `{internalName,label}` refs — for OCR Profiles / Workflow field pickers. */
  fields: JobTypeFieldRef[];
  /** Full draft FieldContent rows — for Zones / LLM tabs that edit per-field config. */
  rawFields: FieldContent[];
  isLoading: boolean;
  /**
   * Apply per-field changes to the draft version content and persist. Forks a
   * draft first if the effective version is published (or none exists), so
   * config is editable regardless of publish state.
   */
  patchFields: (patches: FieldPatch[]) => Promise<void>;
}

/**
 * Field registry for a job type, sourced from the *versioned* Form Config
 * editor (`JobTypeVersion.content.fields`) — draft-first, published as
 * fallback. NOT the legacy `FieldDefinition` table, which is empty until the
 * job type is published. Config-time surfaces (OCR Profiles, Zones, LLM)
 * live on Job Config (renamed from "Processing Config" in WU-10 / R-130)
 * while the job type is still unpublished, so they
 * must read — and write — the draft content directly.
 */
export function useJobTypeRegistryFields(jobTypeId: string): UseJobTypeRegistryFieldsResult {
  const [version, setVersion] = useState<JobTypeVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    async function load() {
      try {
        const versions = await formConfigApi.listVersions(jobTypeId);
        if (cancelled) return;
        const draft = versions.find((v) => v.state === 'draft');
        const effective = draft
          ? await formConfigApi.getVersion(draft.id)
          : await formConfigApi.getCurrentPublished(jobTypeId);
        if (cancelled) return;
        setVersion(effective);
      } catch {
        if (!cancelled) setVersion(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [jobTypeId]);

  const patchFields = useCallback(
    async (patches: FieldPatch[]) => {
      if (patches.length === 0) return;
      const byId = new Map(patches.map((p) => [p.fieldDefinitionId, p.changes]));

      // F-032 — one attempt = fresh server read → keyed patch in V3 space →
      // revision-stamped write. The old implementation wrote a mount-time
      // cached copy flattened to the legacy shape, which (a) clobbered
      // everything authored after mount and (b) structurally dropped V3-only
      // data (per-form ocrExtractionMode, form structure) even when fresh —
      // the two halves of the BW2 config wipe.
      const attempt = async (): Promise<JobTypeVersion> => {
        // listVersions returns full rows (content + contentRevision), so the
        // draft row is usable directly — no second GET needed.
        const versions = await formConfigApi.listVersions(jobTypeId);
        const draft =
          versions.find((v) => v.state === 'draft') ??
          (await formConfigApi.createDraft(jobTypeId));
        const v3 = migrateContentToV3(draft.content as unknown as JobTypeVersionContent);
        const nextV3: JobTypeVersionContentV3 = {
          ...v3,
          forms: v3.forms.map((form) => ({
            ...form,
            fields: form.fields.map((f) =>
              byId.has(f.fieldDefinitionId) ? { ...f, ...byId.get(f.fieldDefinitionId) } : f,
            ),
          })),
        };
        return formConfigApi.updateDraft(draft.id, nextV3, draft.contentRevision);
      };

      let updated: JobTypeVersion;
      try {
        updated = await attempt();
      } catch (e) {
        // A concurrent write landed between our read and write. The patches
        // are tiny and keyed by fieldDefinitionId, so one automatic rebase
        // (re-read + re-apply) is safe; a second conflict surfaces to the caller.
        if (!formConfigApi.isStaleDraftError(e)) throw e;
        updated = await attempt();
      }
      setVersion(updated);
    },
    [jobTypeId],
  );

  const rawFields = version?.content
    ? flattenToLegacyContent(migrateContentToV3(version.content as unknown as JobTypeVersionContent)).fields
    : [];
  const fields: JobTypeFieldRef[] = rawFields.map((f) => ({
    internalName: f.internalName ?? f.name,
    label: f.label,
    isRequired: f.isRequired ?? false,
  }));

  return { fields, rawFields, isLoading, patchFields };
}
